import { FC } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../../components/accordion/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { Switch } from '../../../../components/switch/index';
import { Textarea } from '../../../../components/textarea/index';
import {
  SHARED_CAP_TABLE_NOTE_MAX_LENGTH,
  SharedCapTableSettings,
} from '../../../../hooks/shared-cap-table/shared-cap-table.types';
import { columnTitle } from '../../../../types/snapshot.types';
import { ALL_SHAREABLE_COLUMNS, isColumnShared, toggleSharedColumn } from '../share-columns.util';

const COLUMNS_SECTION = 'columns';
const NOTE_SECTION = 'note';

type ContentTabProps = {
  note: string;
  onNoteChange: (note: string) => void;
  onSettingsChange: (settings: SharedCapTableSettings) => void;
  settings: SharedCapTableSettings;
};

export const ContentTab: FC<ContentTabProps> = ({
  note,
  onNoteChange,
  onSettingsChange,
  settings,
}) => {
  const { columns } = settings;

  return (
    <Accordion className="flex flex-col gap-4" defaultValue={[COLUMNS_SECTION]} type="multiple">
      {/* The grey card carries a 2px inset instead of a border, and the switches sit on their own
          white card inside it — a bordered section drew a line between the hint and the first
          switch, which the design does not have. */}
      <AccordionItem
        className="flex flex-col gap-3 overflow-hidden rounded-md bg-gray-50 px-0.5 pb-0.5 pt-3 data-[state=closed]:pb-3"
        value={COLUMNS_SECTION}
      >
        <AccordionTrigger className="flex-row-reverse px-3 py-0 hover:no-underline">
          <span className="flex flex-col items-start text-left">
            <span className="text-sm font-[550] leading-5 text-gray-700">
              <AppFormattedMessage id="COLUMNS" />
            </span>
            <span className="text-label-sm font-medium text-gray-400">
              <AppFormattedMessage id="SHARE_COLUMNS_HINT" />
            </span>
          </span>
        </AccordionTrigger>

        <AccordionContent className="grid grid-cols-1 gap-3 rounded-md bg-white px-3.5 py-3 shadow-[0px_-1px_3px_0px_rgba(16,24,40,0.04),0px_2px_3px_0px_rgba(16,24,40,0.1),0px_1px_2px_0px_rgba(16,24,40,0.06)] xs:grid-cols-2">
          {ALL_SHAREABLE_COLUMNS.map(
            (columnKey) =>
              columnTitle[columnKey] && (
                <div
                  className="flex items-center justify-between gap-4 rounded-lg border border-gray-100 px-4 py-2.5"
                  key={columnKey}
                >
                  <span className="text-xs font-[450] leading-4 text-gray-700">
                    <AppFormattedMessage id={columnTitle[columnKey]} />
                  </span>
                  <Switch
                    checked={isColumnShared(columns, columnKey)}
                    onChange={() =>
                      onSettingsChange({
                        ...settings,
                        columns: toggleSharedColumn(columns, columnKey),
                      })
                    }
                  />
                </div>
              ),
          )}
        </AccordionContent>
      </AccordionItem>

      {/* Same shell as Columns: grey header card with the note on its own white pane inside. */}
      <AccordionItem
        className="flex flex-col gap-3 overflow-hidden rounded-md bg-gray-50 px-0.5 pb-0.5 pt-3 data-[state=closed]:pb-3"
        value={NOTE_SECTION}
      >
        <AccordionTrigger className="flex-row-reverse px-3 py-0 hover:no-underline">
          <span className="text-sm font-[550] leading-5 text-gray-700">
            <AppFormattedMessage id="SHARE_ADDITIONAL_NOTE" />
          </span>
        </AccordionTrigger>

        <AccordionContent className="flex flex-col gap-2 rounded-md bg-white px-3.5 py-3 shadow-[0px_-1px_3px_0px_rgba(16,24,40,0.04),0px_2px_3px_0px_rgba(16,24,40,0.1),0px_1px_2px_0px_rgba(16,24,40,0.06)]">
          {/* The app's own note field — grey fill under a single hairline, count inside — rather
              than a bordered box: the design uses the same control the event steps do. */}
          <Textarea
            maxLength={SHARED_CAP_TABLE_NOTE_MAX_LENGTH}
            onChange={(event) => onNoteChange(event.target.value)}
            styleVariant="DEFAULT_W_PLACEHOLDER"
            textAreaClassName="min-h-[104px]"
            value={note}
            wrapperClassName="bg-gray-50"
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
